/**
 * Resource API Error Types
 * These error classes match the error types from packages/core/src/core/api.js
 */
export class ResourceApiError extends Error {
	public readonly code: number;
	public readonly opt?: unknown;
	/**
	 * The raw, human-readable message that came from the server response body.
	 * Undefined when the error fell back to a built-in default message.
	 * Populated by `mapHttpErrorToResourceError`.
	 */
	public serverMessage?: string;

	constructor(message: string, code: number, opt?: unknown) {
		super(message);
		this.name = 'ResourceApiError';
		this.code = code;
		this.opt = opt;
	}

	/**
	 * A nullable, user-presentable message, returned verbatim (NOT translated).
	 * - Returns the server-provided message for any status when the backend
	 *   supplied one — this API returns human-readable messages on 4xx and 5xx
	 *   alike (e.g. "Booking has blocking conflicts" arrives as a 409).
	 * - Returns null only when the server gave no message (empty body, network
	 *   failure, timeout), signalling the caller to show its own generic copy.
	 */
	get userMessage(): string | null {
		return this.serverMessage ?? null;
	}
}

export class ResourceNetworkNotConnectedError extends ResourceApiError {
	constructor(message = '', opt?: unknown) {
		super(message, 999, opt);
		this.name = 'ResourceNetworkNotConnectedError';
	}
}

export class ResourceNetworkTimeoutError extends ResourceApiError {
	constructor(message = 'Could not reach the server in time', opt?: unknown) {
		super(message, 408, opt);
		this.name = 'ResourceNetworkTimeoutError';
	}
}

export class ResourceNotFoundError extends ResourceApiError {
	constructor(message = "Could not find what you're looking for", opt?: unknown) {
		super(message, 404, opt);
		this.name = 'ResourceNotFoundError';
	}
}

export class ResourceBadRequestError extends ResourceApiError {
	constructor(message = 'The server did not understand your request', opt?: unknown) {
		super(message, 400, opt);
		this.name = 'ResourceBadRequestError';
	}
}

export class ResourceUnauthorizedError extends ResourceApiError {
	constructor(message = 'Your session has expired', opt?: unknown) {
		super(message, 401, opt);
		this.name = 'ResourceUnauthorizedError';
	}
}

export class ResourceForbiddenError extends ResourceApiError {
	constructor(message = "You don't have sufficient rights", opt?: unknown) {
		super(message, 403, opt);
		this.name = 'ResourceForbiddenError';
	}
}

export class ResourceConflictError extends ResourceApiError {
	constructor(message = 'A conflict was detected', opt?: unknown) {
		super(message, 409, opt);
		this.name = 'ResourceConflictError';
	}
}

export class ResourceUnprocessableEntityError extends ResourceApiError {
	constructor(message = 'Some data is invalid', opt?: unknown) {
		super(message, 422, opt);
		this.name = 'ResourceUnprocessableEntityError';
	}
}

export class ResourceSubscriptionLimitError extends ResourceApiError {
	constructor(message = 'You have reached your subscription limit', opt?: unknown) {
		super(message, 422, opt);
		this.name = 'ResourceSubscriptionLimitError';
	}
}

export class ResourcePaymentRequiredError extends ResourceApiError {
	constructor(message = 'Your subscription has expired', opt?: unknown) {
		super(message, 402, opt);
		this.name = 'ResourcePaymentRequiredError';
	}
}

export class ResourceServerCapacityError extends ResourceApiError {
	constructor(message = 'Back-end server is at capacity', opt?: unknown) {
		super(message, 503, opt);
		this.name = 'ResourceServerCapacityError';
	}
}

export class ResourceGenericApiError extends ResourceApiError {
	constructor(message = 'Something went wrong on the server', opt?: unknown) {
		super(message, 500, opt);
		this.name = 'ResourceGenericApiError';
	}
}

/**
 * Interface for HTTP errors that can be mapped to Resource API errors
 */
export interface HttpErrorLike {
	name?: string;
	message?: string;
	status?: number;
	opt?: {
		message?: string;
		detail?: string;
		status?: number;
	};
}

/**
 * Maps HTTP status codes and error messages to typed Resource API errors
 * Replicates the error handling logic from api.js ApiAjax.prototype._handleAjaxError
 */
export const mapHttpErrorToResourceError = (error: Error & HttpErrorLike): ResourceApiError => {
	const status = error.status || 500;
	const message = error.message || '';
	const opt = error.opt || ('opt' in error ? (error as { opt?: unknown }).opt : undefined);

	// Extract message from opt if available, otherwise use empty string (error classes have defaults)
	let msg = '';
	if (opt && typeof opt === 'object' && 'message' in opt) {
		msg = (opt as { message?: string }).message || '';
	}

	/**
	 * Records the server-origin message (if any) on the created error so that
	 * `ResourceApiError.userMessage` can surface it verbatim. Reads `msg` at call
	 * time so branches that re-derive it (e.g. 422) attach the final value.
	 */
	const attachServerMessage = (err: ResourceApiError): ResourceApiError => {
		if (msg) {
			err.serverMessage = msg;
		}
		return err;
	};

	if (error.name === 'AbortError') {
		return error as unknown as ResourceApiError;
	}

	if (message === 'timeout' || status === 408) {
		// Use empty string - ResourceNetworkTimeoutError has a default message
		return attachServerMessage(new ResourceNetworkTimeoutError(msg || undefined, opt));
	}

	if (status === 422) {
		const messageLower = message.toLowerCase();
		if (messageLower.indexOf('limit') >= 0 && messageLower.indexOf('reach') >= 0) {
			// Use empty string - ResourceSubscriptionLimitError has a default message
			return attachServerMessage(new ResourceSubscriptionLimitError(msg || undefined, opt));
		}

		// Parse error message to disaply to users
		if (message && message != '') {
			const splitIndex = message.indexOf(':');
			if (splitIndex > 0 && splitIndex < 10) {
				msg = message.substring(splitIndex + 1).trim();
			} else {
				msg = message.trim();
			}
		}
		return attachServerMessage(new ResourceUnprocessableEntityError(msg || undefined, opt));
	}

	switch (status) {
		case 400:
			// Use empty string - ResourceBadRequestError has a default message
			return attachServerMessage(new ResourceBadRequestError(msg || undefined, opt));
		case 401:
			// Use empty string - ResourceUnauthorizedError has a default message
			return attachServerMessage(new ResourceUnauthorizedError(msg || undefined, opt));
		case 402:
			// Use empty string - ResourcePaymentRequiredError has a default message
			return attachServerMessage(new ResourcePaymentRequiredError(msg || undefined, opt));
		case 403:
			// Use empty string - ResourceForbiddenError has a default message
			return attachServerMessage(new ResourceForbiddenError(msg || undefined, opt));
		case 404:
			// Use empty string - ResourceNotFoundError has a default message
			return attachServerMessage(new ResourceNotFoundError(msg || undefined, opt));
		case 409:
			// Use empty string - ResourceConflictError has a default message
			return attachServerMessage(new ResourceConflictError(msg || undefined, opt));
		case 408:
			// Use empty string - ResourceNetworkTimeoutError has a default message
			return attachServerMessage(new ResourceNetworkTimeoutError(msg || undefined, opt));
		case 503:
			// Use empty string - ResourceServerCapacityError has a default message
			return attachServerMessage(new ResourceServerCapacityError(msg || undefined, opt));
		case 500:
		default:
			// Use empty string - ResourceGenericApiError has a default message
			return attachServerMessage(new ResourceGenericApiError(msg || undefined, opt));
	}
};
